import { EntityType } from '@frontend/common';
import { DefaultSecondaryButton, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConstraintExtendedClient } from '@frontend/constraint/api';
import { ConstraintExtended } from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { ModuleExtended } from '@frontend/module/types';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { formatConstraintData } from '../../../constraint/modal/constraint-detail-sections';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { SlotsGrid } from './slots-grid/slots-grid.component';

export interface ModuleDetailSectionsProps {
    module: ModuleExtended;
    dispatch: ThunkDispatch<unknown, unknown, Action>;
    viewDetailedConstraintsList: (moduleId: string) => void;
}

/** Shared MODULE general extras (slots grid) — used by ReactiveDetailShell. */
export const ModuleGeneralSection = ({ module }: { module: ModuleExtended }) => (
    <div className='flex flex-col gap-4'>
        <SlotsGrid moduleId={module.id} />
    </div>
);

export const ModuleActivitySection = ({ module, dispatch }: ModuleDetailSectionsProps) => (
    <ReactiveObjectList<ExtendedPudoTransaction>
        key='module-activity'
        fetch={(arg) =>
            TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                account_id: module.account_id,
                module_id: module.id,
                size: '8',
                ...arg,
                without_collis: 'true',
                without_packages: 'true',
                without_pin_codes: 'true'
            })
        }
        label={
            <FormattedMessage
                id='pudo.active-transactions'
                description='Label for lists of active PuDo transactions shown in activity sections'
                defaultMessage='Active transactions'
            />
        }
        columns={{
            columns: ['senders', 'receivers', 'state'],
            headerOverwrite: new Map([
                ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
            ]),
            customCells: new Map([
                [
                    'senders',
                    (cell) => {
                        const senders = cell.row.original.senders;
                        if (senders === null || senders.length === 0) {
                            return (
                                <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                </td>
                            );
                        }
                        return (
                            <td
                                key={senders[0].entity_id}
                                className='text-brand-primary align-center truncate p-2 text-xs'>
                                <div className='flex gap-2.5 truncate text-sm font-medium'>
                                    <PlatformObjectBadge
                                        id={senders[0].entity_id}
                                        entityType={EntityType.USER}
                                        display={senders[0].name}
                                        foreign={senders[0].foreign}
                                        accountId={accountIdFromResolvedEntity(senders[0])}
                                    />
                                    {senders.length > 1 && (
                                        <MultiEntityButton
                                            entities={senders.slice(1)}
                                            render={(entity) => (
                                                <PlatformObjectBadge
                                                    id={entity.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={entity.name}
                                                    foreign={entity.foreign}
                                                    accountId={accountIdFromResolvedEntity(entity)}
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                            </td>
                        );
                    }
                ],
                [
                    'receivers',
                    (cell) => {
                        const receivers = cell.row.original.receivers;
                        if (receivers === null || receivers.length === 0) {
                            return (
                                <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                </td>
                            );
                        }
                        return (
                            <td
                                key={receivers[0].entity_id}
                                className='text-brand-primary align-center truncate p-2 text-xs'>
                                <div className='flex gap-2.5 truncate text-sm font-medium'>
                                    <PlatformObjectBadge
                                        id={receivers[0].entity_id}
                                        entityType={EntityType.USER}
                                        display={receivers[0].name}
                                        foreign={receivers[0].foreign}
                                        accountId={accountIdFromResolvedEntity(receivers[0])}
                                    />
                                    {receivers.length > 1 && (
                                        <MultiEntityButton
                                            entities={receivers.slice(1)}
                                            render={(entity) => (
                                                <PlatformObjectBadge
                                                    id={entity.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={entity.name}
                                                    foreign={entity.foreign}
                                                    accountId={accountIdFromResolvedEntity(entity)}
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                            </td>
                        );
                    }
                ],
                [
                    'state',
                    (cell) => (
                        <td
                            key={cell.row.original.state}
                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                            <TransactionStateBadge state={cell.row.original.state} />
                        </td>
                    )
                ]
            ])
        }}
        dispatch={dispatch}
        type={EntityType.TRANSACTION}
        tableProps={{ hideFooter: true, maxHeight: true }}
    />
);

export const ModuleContentsSection = ({ module, dispatch }: ModuleDetailSectionsProps) => (
    <ReactiveObjectList<StockExtended>
        key='module-content'
        label={CommonMessage.OBJECTS.STOCK.DEFAULT}
        fetch={(arg) => StockExtendedClient.fetchSpotModuleStocksExtended(module.account_id, module.spot_id, module.id, arg)}
        columns={{
            columns: ['product', 'ean', 'sku', 'slot'],
            customCells: new Map([
                [
                    'product',
                    (cell) =>
                        cell.row.original.product ? (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    id={cell.row.original.product.entity_id}
                                    display={cell.row.original.product.name}
                                    entityType={EntityType.PRODUCT}
                                    key={cell.row.original.product.entity_id}
                                    foreign={cell.row.original.product.foreign}
                                    accountId={accountIdFromResolvedEntity(cell.row.original.product)}
                                />
                            </td>
                        ) : (
                            <td>/</td>
                        )
                ],
                [
                    'slot',
                    (cell) =>
                        cell.row.original.slot ? (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    id={cell.row.original.slot.entity_id}
                                    display={cell.row.original.slot.name}
                                    entityType={EntityType.SLOT}
                                    parentIds={{ spot_id: module.spot_id, module_id: module.id }}
                                    key={cell.row.original.slot.entity_id}
                                    foreign={cell.row.original.slot.foreign}
                                    accountId={accountIdFromResolvedEntity(cell.row.original.slot)}
                                />
                            </td>
                        ) : (
                            <td className={defaultCellClassName}>/</td>
                        )
                ]
            ])
        }}
        dispatch={dispatch}
        type={EntityType.STOCK}
        tableProps={{ hideFooter: true, defaultHeight: true }}
    />
);

export const ModuleConstraintsSection = ({ module, dispatch, viewDetailedConstraintsList }: ModuleDetailSectionsProps) => (
    <ReactiveObjectList<ConstraintExtended>
        key='module-constraints'
        label={CommonMessage.OBJECTS.CONSTRAINT.PLURAL}
        fetch={(arg) => ConstraintExtendedClient.fetchExtendedConstraints({ ...arg, module_id: module.id, account_id: module.account_id })}
        type={EntityType.CONSTRAINT}
        columns={{
            columns: ['module', 'slot', 'data'],
            headerOverwrite: new Map([['data', CommonMessage.OBJECTS.CONSTRAINT.DEFAULT]]),
            customCells: new Map([
                [
                    'module',
                    (cell) => (
                        <td className={defaultCellClassName}>
                            {cell.row.original.module ? (
                                <PlatformObjectBadge
                                    id={cell.row.original.module.entity_id}
                                    display={cell.row.original.module.name}
                                    entityType={EntityType.MODULE}
                                    parentIds={{ spot_id: module.spot_id }}
                                    key={cell.row.original.module.entity_id}
                                    foreign={cell.row.original.module.foreign}
                                    accountId={accountIdFromResolvedEntity(cell.row.original.module)}
                                />
                            ) : (
                                <span>/</span>
                            )}
                        </td>
                    )
                ],
                [
                    'slot',
                    (cell) => (
                        <td className={defaultCellClassName}>
                            {cell.row.original.slot ? (
                                <PlatformObjectBadge
                                    id={cell.row.original.slot.entity_id}
                                    display={cell.row.original.slot.name}
                                    entityType={EntityType.SLOT}
                                    parentIds={{ spot_id: module.spot_id, module_id: module.id }}
                                    key={cell.row.original.slot.entity_id}
                                    foreign={cell.row.original.slot.foreign}
                                    accountId={accountIdFromResolvedEntity(cell.row.original.slot)}
                                />
                            ) : (
                                <span>/</span>
                            )}
                        </td>
                    )
                ],
                [
                    'data',
                    (cell) => (
                        <td className={defaultCellClassName}>
                            <div className='flex gap-1'>
                                <span>{(cell.row.original.type.charAt(0).toUpperCase() + cell.row.original.type.slice(1)).replace(/_/g, ' ')}:</span>
                                <span>{formatConstraintData(cell.row.original.data, cell.row.original.type)}</span>
                            </div>
                        </td>
                    )
                ]
            ])
        }}
        dispatch={dispatch}
        tableProps={{
            defaultHeight: true,
            customFooter: (
                <div className='flex gap-3.5'>
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.VIEW_DETAILED_LIST}
                        onClick={() => viewDetailedConstraintsList(module.id)}
                    />
                </div>
            )
        }}
    />
);
